import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiRequisition } from '@Model';
import { RequisitionsRepository } from '@Repositories/Auftragsbearbeitung';
import {
    type AsyncStateUpdate,
    EditFrameMode,
    type IFrameBodyProps,
    isOk,
    useQueuedState,
    useToasts,
    useUpdateServerObject,
} from '@synqo/frontend-component-library';
import { useQueryClient } from '@tanstack/react-query';
import { type RefObject, useCallback, useMemo, useRef } from 'react';

export interface RequisitionDraft {
    /** Frame-body props ready to feed into wizard step bodies. */
    frameProps: IFrameBodyProps<ApiRequisition>;
    /** Commits the current draft via the backend's `:commit` endpoint and calls `onCommitted` on success. */
    submit: () => Promise<void>;
    /** Mutable flag that flips true on the first server-backed edit and false after a successful commit. */
    hasUnsavedChangesRef: RefObject<boolean>;
}

export interface UseRequisitionDraftOptions {
    prefilled: ApiRequisition;
    apiResourcePath: string;
    /** Called after the draft is committed successfully. Typically used to navigate to the new entity. */
    onCommitted: (committed: ApiRequisition) => void;
}

/**
 * Owns the backend lifecycle of a single requisition draft: queued state, per-edit saves, and the final commit.
 */
// @ai-generated 2026-05-28T13:00:50+00:00 by matthias.gessinger@synqony.com using Claude Haiku
export function useRequisitionDraft({
    prefilled,
    apiResourcePath,
    onCommitted,
}: UseRequisitionDraftOptions): RequisitionDraft {
    const { pushAllToasts } = useToasts();
    const queryClient = useQueryClient();
    const { state, requestSetState, queueDepth } = useQueuedState<ApiRequisition>(prefilled);
    const updateServerObject = useUpdateServerObject<ApiRequisition>(apiResourcePath);

    const requisitionRepository = useAuftragRepository(RequisitionsRepository);

    const hasUnsavedChangesRef = useRef(false);

    const requestUpdateState = useCallback<AsyncStateUpdate<ApiRequisition>>(
        async (updater) =>
            requestSetState(async (latest) => {
                const result = await updater(latest, updateServerObject);
                if (result === undefined) {
                    return latest;
                }
                hasUnsavedChangesRef.current = true;
                pushAllToasts(result.messages);
                return result.data ?? latest;
            }),
        [requestSetState, updateServerObject, pushAllToasts],
    );

    /**
     * Drops the requisition from the query cache.
     */
    // @ai-generated 2026-09-14T08:24:01+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
    const forgetCachedDraft = useCallback(
        (draftId: string) => queryClient.removeQueries({ queryKey: [apiResourcePath, draftId] }),
        [queryClient, apiResourcePath],
    );

    const submit = useCallback(async () => {
        await requestSetState(async (latest) => {
            const result = await requisitionRepository.commitDraft(latest.id);
            pushAllToasts(result.messages);
            if (!isOk(result)) {
                return latest;
            }
            hasUnsavedChangesRef.current = false;
            forgetCachedDraft(latest.id);
            onCommitted(result.data);
            return result.data;
        });
    }, [requestSetState, requisitionRepository, pushAllToasts, forgetCachedDraft, onCommitted]);

    const frameProps = useMemo<IFrameBodyProps<ApiRequisition>>(
        () => ({
            mode: EditFrameMode.Create,
            data: state,
            meta: undefined,
            apiResourcePath,
            requestUpdateState,
            stateQueueDepth: queueDepth,
            refetchData: () => Promise.resolve(true),
            events: {},
            embedded: false,
        }),
        [state, apiResourcePath, requestUpdateState, queueDepth],
    );

    return { frameProps, submit, hasUnsavedChangesRef };
}
